import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Breadcrums from "../../common/breadcrumbs";
import {
    globalLoader,
    handleServerValidations,
} from "../../../../utils/commonfunction";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { Update, Details } from "../../services/video.services";

const EditVideo = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t } = useTranslation();
    const [submitted, setSubmitted] = useState(false);
    const [previewVideo, setPreviewVideo] = useState(null);
    const [existingVideo, setExistingVideo] = useState(null);
    const [previewThumbnail, setPreviewThumbnail] = useState(null);
    const [existingThumbnail, setExistingThumbnail] = useState(null);
    const [initialData, setInitialData] = useState({
        title: "",
        description: "",
        status: 1,
        video_file: null,
        thumbnail_image: null,
    });

    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        { title: "Videos", url: "/admin/video/list/1" },
        { title: t("btn_edit"), url: "" },
    ];

    useEffect(() => {
        if (id) {
            globalLoader(true);
            Details(id)
                .then((response) => {
                    if (response.success) {
                        const data = response.data;
                        setInitialData({
                            title: data.title || "",
                            description: data.description || "",
                            status: (data.status === true || data.status === 1) ? 1 : 0,
                            video_file: null,
                            thumbnail_image: null,
                        });

                        if (data.video_file) {
                            setExistingVideo(data.video_file);
                        }
                        if (data.thumbnail_image) {
                            setExistingThumbnail(data.thumbnail_image);
                        }
                    }
                    globalLoader(false);
                })
                .catch((error) => {
                    console.log("error fetching details", error);
                    globalLoader(false);
                });
        }
    }, [id]);

    const FILE_SIZE_LIMIT = 250 * 1024 * 1024; // 250MB

    const validationSchema = Yup.object().shape({
        title: Yup.string().trim().required("Title is required").max(255, "Title must be at most 255 characters"),
        description: Yup.string().trim().max(1000, "Description must be at most 1000 characters"),
        video_file: Yup.mixed().nullable().test("fileSize", "File size is too large (max 250MB)", (value) => {
            if (!value) return true; // file is optional on edit
            return value.size <= FILE_SIZE_LIMIT;
        }),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: initialData,
        validationSchema: validationSchema,
        onSubmit: (values) => {
            globalLoader(true);
            setSubmitted(true);

            const formData = new FormData();
            formData.append("id", id);
            formData.append("title", values.title);
            formData.append("description", values.description);
            formData.append("status", values.status);

            if (values.video_file) {
                formData.append("video_file", values.video_file);
            }
            if (values.thumbnail_image) {
                formData.append("thumbnail_image", values.thumbnail_image);
            }

            Update(formData)
                .then((response) => {
                    if (response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message,
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setTimeout(() => {
                            navigate(`/admin/video/list/1`);
                        }, 1000);
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(response),
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setSubmitted(false);
                    }
                })
                .catch((error) => {
                    console.log("error ====> ", error);
                    globalLoader(false);
                    setSubmitted(false);
                });
        },
    });

    const handleVideoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            formik.setFieldValue("video_file", file);
            const reader = new FileReader();
            reader.onload = (ev) => setPreviewVideo(ev.target.result);
            reader.readAsDataURL(file);
        }
    };

    const handleThumbnailChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            formik.setFieldValue("thumbnail_image", file);
            const reader = new FileReader();
            reader.onload = (ev) => setPreviewThumbnail(ev.target.result);
            reader.readAsDataURL(file);
        }
    };

    const currentVideoSrc = previewVideo || existingVideo;
    const currentThumbnailSrc = previewThumbnail || existingThumbnail;

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card">
                    <div className="card-body">
                        <h6 className="main-content-label mb-3">Edit Video</h6>
                        <form onSubmit={formik.handleSubmit}>
                            <div className="row row-sm">
                                {/* Title */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="title" className="text-left d-flex">
                                        Title :<span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name="title"
                                        id="title"
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter video title"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.title}
                                    />
                                    {formik.errors.title && formik.touched.title && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.title}
                                        </span>
                                    )}
                                </div>

                                {/* Status */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="status" className="text-left d-flex">
                                        Status :
                                    </label>
                                    <select
                                        name="status"
                                        id="status"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.status}
                                    >
                                        <option value={1}>{t("Active")}</option>
                                        <option value={0}>{t("Inactive")}</option>
                                    </select>
                                </div>

                                {/* Video File */}
                                <div className="col-lg-12 form-group">
                                    <label htmlFor="video_file" className="text-left d-flex">
                                        Video File :
                                    </label>
                                    <input
                                        name="video_file"
                                        id="video_file"
                                        type="file"
                                        className="form-control"
                                        accept="video/*"
                                        onChange={handleVideoChange}
                                    />
                                    {formik.errors.video_file && formik.touched.video_file && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.video_file}
                                        </span>
                                    )}
                                    {currentVideoSrc && (
                                        <div className="mt-2 text-left">
                                            <video
                                                controls
                                                style={{
                                                    maxWidth: "300px",
                                                    maxHeight: "300px",
                                                    width: "100%",
                                                    height: "auto",
                                                    borderRadius: "8px",
                                                    border: "1px solid #dee2e6",
                                                    objectFit: "contain",
                                                    backgroundColor: "#000"
                                                }}
                                            >
                                                <source src={currentVideoSrc} />
                                                Your browser does not support the video tag.
                                            </video>
                                        </div>
                                    )}
                                </div>

                                {/* Thumbnail Image */}
                                <div className="col-lg-12 form-group">
                                    <label htmlFor="thumbnail_image" className="text-left d-flex">
                                        Thumbnail Image :
                                    </label>
                                    <input
                                        name="thumbnail_image"
                                        id="thumbnail_image"
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        onChange={handleThumbnailChange}
                                    />
                                    {currentThumbnailSrc && (
                                        <div className="mt-2 text-left">
                                            <img
                                                src={currentThumbnailSrc}
                                                alt="Thumbnail Preview"
                                                style={{
                                                    maxWidth: "150px",
                                                    maxHeight: "150px",
                                                    width: "100%",
                                                    height: "auto",
                                                    borderRadius: "8px",
                                                    border: "1px solid #dee2e6",
                                                    objectFit: "contain",
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Description */}
                                <div className="col-lg-12 form-group">
                                    <label htmlFor="description" className="text-left d-flex">
                                        Description :
                                    </label>
                                    <textarea
                                        name="description"
                                        id="description"
                                        className="form-control"
                                        rows="4"
                                        placeholder="Enter video description"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.description}
                                    ></textarea>
                                    {formik.errors.description && formik.touched.description && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.description}
                                        </span>
                                    )}
                                </div>

                                <div className="col-md-12">
                                    <button
                                        className="btn btn-main-primary signbtn mr-2"
                                        type="submit"
                                        disabled={submitted}
                                    >
                                        <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                                        {t("btn_update")}
                                    </button>
                                    <button
                                        className="btn ripple btn-secondary"
                                        type="button"
                                        onClick={() => navigate(-1)}
                                    >
                                        <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                                        {t("btn_cancel")}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EditVideo;
